import React from 'react';
import { Socket, io } from 'socket.io-client';
import UserContext from './userContext';

const SocketContext = React.createContext<Socket | undefined>(undefined);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
    const [socket, setSocket] = React.useState<Socket>();
    const [joined, setJoined] = React.useState(false);

    const user = React.useContext(UserContext);

    const handleSocketReconnect = React.useCallback(() => {
        if (socket) {
            socket.open();
            // console.log('RECONNECT SOCKET - OPEN');
        } else {
            const skt = io();
            skt.on('disconnect', handleSocketReconnect);
            skt.on('connect_timeout', handleSocketReconnect);
            setSocket(skt);
            // console.log('RECONNECT SOCKET - NEW SOCKET');
        }
    }, [socket]);

    const handleSocketConnect = React.useCallback(() => {
        if (!socket) {
            const skt = io();
            skt.on('disconnect', handleSocketReconnect);
            skt.on('connect_timeout', handleSocketReconnect);
            setSocket(skt);
            // console.log('CONNECT SOCKET');
        }
    }, [socket, handleSocketReconnect]);

    const handleSocketDisconnect = React.useCallback(() => {
        if (socket) {
            socket.removeListener('disconnect', handleSocketReconnect);
            socket.removeListener('connect_timeout', handleSocketReconnect);
            socket.close();
            setSocket(undefined);
            // console.log('DISCONNECT SOCKET');
        }
    }, [socket, handleSocketReconnect]);

    const joinRoom = React.useCallback(() => {
        if (!joined && socket && user.activeClient) {
            setJoined(true);
            socket.emit(
                'join room',
                `${user.activeClient.accountName.toLowerCase().replace(' ', '-')}`,
            );
            socket.emit(
                'online',
                user.activeClient.userClients.userType,
                user.activeClient.userClients.influencerId,
            );
            // console.log('JOIN BRAND ROOM');
        }
    }, [joined, socket, user]);

    const leaveRoom = React.useCallback(() => {
        if (joined && socket && user.activeClient) {
            setJoined(false);
            socket.emit(
                'leave room',
                `${user.activeClient.accountName.toLowerCase().replace(' ', '-')}`,
            );
            socket.emit(
                'offline',
                user.activeClient.userClients.userType,
                user.activeClient.userClients.influencerId,
            );
            // console.log('LEAVE BRAND ROOM');
        }
    }, [socket, joined, user]);

    React.useEffect(() => {
        handleSocketConnect();
        return () => handleSocketDisconnect();
    }, [handleSocketConnect, handleSocketDisconnect]);

    React.useEffect(() => {
        joinRoom();
        return () => leaveRoom();
    }, [joinRoom, leaveRoom]);

    return <SocketContext.Provider value={socket}>{children}</SocketContext.Provider>;
};

export default SocketContext;
